import { ref, onUnmounted } from '@vue/composition-api';

/**
 * Vue2 版 WebSocket 连接 composable
 * @param {Object} authInfo - { doctorId, conversationId }
 * sessionId 已移除：后端直接从 WS 握手时浏览器自动携带的 Cookie 中读取（HttpOnly cookie 前端 JS 无法读取）
 */
export function useAgentWebSocket(authInfo) {
  const ws = ref(null);
  const status = ref('disconnected'); // 'connecting' | 'connected' | 'disconnected' | 'error'
  const reconnectTimer = ref(null);
  const heartbeatTimer = ref(null);
  let onMessageCallback = null;
  let manualDisconnect = false;
  // 每次 connect() 生成新 token，用于丢弃旧连接的消息，防止流式内容串到新会话
  let currentSessionToken = null;
  // 是否禁止自动重连（登录失效时置为 true，避免无限重连）
  let reconnectForbidden = false;

  function connect() {
    cleanup();
    manualDisconnect = false;
    reconnectForbidden = false; // 主动 connect 时重置禁止标志

    // 生成本次连接的唯一 token
    const myToken = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    currentSessionToken = myToken;

    const { doctorId, conversationId } = authInfo;
    console.log('[DoctorAgent] connect() 被调用, 参数:', { doctorId, conversationId });
    if (!doctorId) {
      status.value = 'error';
      console.error('[DoctorAgent] WS 连接缺少必要参数: doctorId 为空');
      return;
    }

    // 本地开发走代理 /doctor-channel/ws，生产环境走同域
    // sessionId 不再通过 URL 参数传递，后端直接读取 WS 握手时浏览器自动携带的 Cookie
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const baseUrl = `${wsProtocol}://${window.location.host}/doctor-channel/ws`;

    // doctorId 仍需传递，后端在 getUserInfo 返回前需要临时标识
    const doctorIdEncoded = btoa(unescape(encodeURIComponent(String(doctorId))));
    const conversationParam = conversationId
      ? `&conversationId=${encodeURIComponent(conversationId)}`
      : '';

    const url = `${baseUrl}?doctorId=${encodeURIComponent(doctorIdEncoded)}${conversationParam}`;

    status.value = 'connecting';
    const socket = new WebSocket(url);

    socket.onopen = () => {
      // 如果 token 已被新连接替换，关闭此连接
      if (currentSessionToken !== myToken) {
        socket.close();
        return;
      }
      status.value = 'connected';
      startHeartbeat(socket);
    };

    socket.onmessage = (event) => {
      // token 不匹配说明已有新连接，丢弃旧连接的所有消息
      if (currentSessionToken !== myToken) return;
      try {
        const data = JSON.parse(event.data);
        // 服务端检测到同一 conversationId 有新连接时，会先发此消息再关闭旧连接
        // 收到后禁止重连，避免 onclose 触发 scheduleReconnect 形成死循环
        // 注意：这是正常的"新连接顶替旧连接"场景，不应显示错误状态
        if (data.type === 'session_replaced') {
          reconnectForbidden = true;
          manualDisconnect = true;
          return;
        }
        onMessageCallback && onMessageCallback(data);
      } catch (e) {
        // ignore parse errors
      }
    };

    socket.onclose = () => {
      if (currentSessionToken !== myToken) return;
      status.value = 'disconnected';
      stopHeartbeat();
      if (!manualDisconnect && !reconnectForbidden) {
        scheduleReconnect();
      }
    };

    socket.onerror = () => {
      if (currentSessionToken !== myToken) return;
      status.value = 'error';
      // 先置 null 再 close，避免 onclose 触发重连
      ws.value = null;
      try { socket.close(); } catch (e) { /* ignore */ }
    };

    ws.value = socket;
  }

  function send(data) {
    if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  function onMessage(callback) {
    onMessageCallback = callback;
  }

  function cleanup() {
    if (reconnectTimer.value) {
      clearTimeout(reconnectTimer.value);
      reconnectTimer.value = null;
    }
    stopHeartbeat();
    if (ws.value) {
      try {
        ws.value.onclose = null;
        ws.value.onerror = null;
        ws.value.onmessage = null;
        ws.value.onopen = null;
        ws.value.close();
      } catch (e) {
        // ignore
      }
      ws.value = null;
    }
  }

  function disconnect() {
    manualDisconnect = true;
    // 使当前 token 失效，后续旧连接的消息全部丢弃
    currentSessionToken = null;
    cleanup();
    status.value = 'disconnected';
  }

  /**
   * 禁止自动重连（登录失效时调用），同时断开当前连接
   * 调用 connect() 会重置此标志
   */
  function stopReconnect() {
    reconnectForbidden = true;
    disconnect();
    status.value = 'error';
  }

  function scheduleReconnect() {
    if (reconnectTimer.value) return;
    reconnectTimer.value = setTimeout(() => {
      reconnectTimer.value = null;
      connect();
    }, 3000);
  }

  function startHeartbeat(socket) {
    stopHeartbeat();
    heartbeatTimer.value = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 25000);
  }

  function stopHeartbeat() {
    if (heartbeatTimer.value) {
      clearInterval(heartbeatTimer.value);
      heartbeatTimer.value = null;
    }
  }

  onUnmounted(() => {
    disconnect();
  });

  return {
    status,
    connect,
    send,
    onMessage,
    disconnect,
    stopReconnect,
  };
}
